/**
 * ============================================================================
 * KbImportPage — Wizard de importação em lote (Markdown / PDF) para a KB
 * ----------------------------------------------------------------------------
 * F3 · Importadores
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 — Versão inicial (3 passos: fonte, destino, executar).
 *
 * Segurança: escopado ao tenant ativo do usuário (get_validated_tenant_id no backend).
 * Limites: 50 arquivos por lote, 20 MB por arquivo.
 * ============================================================================
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, FileText, FileType, Upload, CheckCircle2, XCircle,
  AlertTriangle, ExternalLink, Loader2, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useKbSpaces } from "@/hooks/kb/useKb";
import { useKbImportMarkdown, useKbImportPdf, type ImportResult } from "@/hooks/kb/useKbImport";

const MAX_FILES = 50;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

type Format = "markdown" | "pdf";
type Classification = "public" | "internal" | "confidential" | "restricted";
type AiVisibility = "public" | "tenant" | "none" | "inherit";

// Formata bytes em KB/MB
const formatSize = (b: number) =>
  b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

export default function KbImportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillSpaceId = searchParams.get("space") ?? "";

  const { data: spaces = [], isLoading: loadingSpaces } = useKbSpaces();
  const importMd = useKbImportMarkdown();
  const importPdf = useKbImportPdf();

  // ---------- Estado do wizard ----------
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [format, setFormat] = useState<Format>("markdown");
  const [files, setFiles] = useState<File[]>([]);
  const [spaceId, setSpaceId] = useState<string>(prefillSpaceId);
  const [classification, setClassification] = useState<Classification>("internal");
  const [aiVisibility, setAiVisibility] = useState<AiVisibility>("tenant");
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const running = importMd.isPending || importPdf.isPending;

  // ---------- Handlers de arquivos ----------
  const acceptExt = format === "markdown" ? ".md,.markdown,text/markdown,text/plain" : ".pdf,application/pdf";

  const handlePickFiles = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);
    const valid: File[] = [];
    const errors: string[] = [];
    for (const f of incoming) {
      const okExt = format === "markdown"
        ? /\.(md|markdown|txt)$/i.test(f.name)
        : /\.pdf$/i.test(f.name);
      if (!okExt) { errors.push(`${f.name}: extensão inválida`); continue; }
      if (f.size > MAX_FILE_SIZE) { errors.push(`${f.name}: excede 20 MB`); continue; }
      valid.push(f);
    }
    const merged = [...files, ...valid].slice(0, MAX_FILES);
    setFiles(merged);
    if (errors.length) {
      // Feedback simples inline (sem toast pra não poluir)
      console.warn("Arquivos rejeitados:", errors);
    }
  };

  const removeFile = (idx: number) => setFiles(files.filter((_, i) => i !== idx));

  // ---------- Validação por passo ----------
  const canNext = useMemo(() => {
    if (step === 1) return files.length > 0;
    if (step === 2) return !!spaceId;
    return true;
  }, [step, files, spaceId]);

  // ---------- Execução ----------
  const handleExecute = async () => {
    setResult(null);
    setUploadProgress({ done: 0, total: files.length });
    const params = {
      space_id: spaceId,
      data_classification: classification,
      ai_visibility: aiVisibility,
      files,
      onProgress: (done: number, total: number) => setUploadProgress({ done, total }),
    };
    try {
      const data = format === "markdown"
        ? await importMd.mutateAsync(params)
        : await importPdf.mutateAsync(params);
      setResult(data);
    } catch {
      // toast já disparado pelo hook
    } finally {
      setUploadProgress(null);
    }
  };

  const resetWizard = () => {
    setStep(1); setFiles([]); setResult(null); setUploadProgress(null);
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/tenant/kb")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Importar em lote</h1>
          <p className="text-sm text-muted-foreground">
            Envie múltiplos arquivos Markdown ou PDF para criar páginas rascunho na Base de Conhecimento.
          </p>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-sm">
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full border ${
              step >= n ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground"
            }`}>{n}</div>
            <span className={step === n ? "font-medium" : "text-muted-foreground"}>
              {n === 1 ? "Fonte" : n === 2 ? "Destino" : "Executar"}
            </span>
            {n < 3 && <div className="mx-2 h-px w-8 bg-border" />}
          </div>
        ))}
      </div>

      {/* Body */}
      <Card className="flex-1 overflow-auto p-4 md:p-6">
        {/* ============================= STEP 1 ============================ */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label className="text-base">Formato dos arquivos</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => { setFormat("markdown"); setFiles([]); }}
                  className={`flex items-center gap-3 rounded-lg border p-4 text-left transition ${
                    format === "markdown" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <FileText className="h-8 w-8 text-primary" />
                  <div>
                    <div className="font-medium">Markdown (.md)</div>
                    <div className="text-xs text-muted-foreground">
                      1 arquivo = 1 página. Suporta H1-H6, listas, código, citações.
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => { setFormat("pdf"); setFiles([]); }}
                  className={`flex items-center gap-3 rounded-lg border p-4 text-left transition ${
                    format === "pdf" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <FileType className="h-8 w-8 text-primary" />
                  <div>
                    <div className="font-medium">PDF (.pdf)</div>
                    <div className="text-xs text-muted-foreground">
                      1 PDF = 1 página. Requer camada de texto (não escaneado).
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-base">Arquivos ({files.length}/{MAX_FILES})</Label>
              <label className="mt-2 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center hover:bg-muted/50">
                <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
                <span className="text-sm">
                  Clique para selecionar ou arraste arquivos
                  <span className="block text-xs text-muted-foreground">
                    Máximo {MAX_FILES} arquivos, 20 MB cada.
                  </span>
                </span>
                <input
                  type="file"
                  multiple
                  accept={acceptExt}
                  className="hidden"
                  onChange={(e) => handlePickFiles(e.target.files)}
                />
              </label>

              {files.length > 0 && (
                <div className="mt-3 max-h-72 space-y-1 overflow-auto rounded border">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 border-b px-3 py-2 text-sm last:border-b-0">
                      <div className="min-w-0 flex-1 truncate">{f.name}</div>
                      <div className="text-xs text-muted-foreground">{formatSize(f.size)}</div>
                      <Button variant="ghost" size="icon" onClick={() => removeFile(i)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================= STEP 2 ============================ */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label>Space de destino *</Label>
              <Select value={spaceId} onValueChange={setSpaceId} disabled={loadingSpaces}>
                <SelectTrigger><SelectValue placeholder="Selecione um space" /></SelectTrigger>
                <SelectContent>
                  {spaces.map((s: any) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                As páginas serão criadas como <b>rascunho</b> — publique manualmente no editor após revisar.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Classificação de dados</Label>
                <Select value={classification} onValueChange={(v) => setClassification(v as Classification)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Público</SelectItem>
                    <SelectItem value="internal">Interno</SelectItem>
                    <SelectItem value="confidential">Confidencial</SelectItem>
                    <SelectItem value="restricted">Restrito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visibilidade para IA</Label>
                <Select value={aiVisibility} onValueChange={(v) => setAiVisibility(v as AiVisibility)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tenant">Somente minha empresa</SelectItem>
                    <SelectItem value="public">Público (IAs externas)</SelectItem>
                    <SelectItem value="none">Não indexar para IA</SelectItem>
                    <SelectItem value="inherit">Herdar do espaço</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Resumo</AlertTitle>
              <AlertDescription>
                <b>{files.length}</b> arquivo(s) {format === "markdown" ? "Markdown" : "PDF"} serão importados.
                Duplicatas (mesmo checksum) são automaticamente puladas.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* ============================= STEP 3 ============================ */}
        {step === 3 && (
          <div className="space-y-4">
            {!running && !result && (
              <div className="text-center">
                <p className="mb-4 text-sm text-muted-foreground">
                  Pronto para importar <b>{files.length}</b> arquivo(s). Clique abaixo para iniciar.
                </p>
                <Button onClick={handleExecute} size="lg">
                  <Upload className="mr-2 h-4 w-4" /> Iniciar importação
                </Button>
              </div>
            )}

            {running && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {uploadProgress
                    ? `Enviando arquivos… ${uploadProgress.done}/${uploadProgress.total}`
                    : "Processando arquivos no servidor…"}
                </div>
                {uploadProgress && (
                  <Progress value={(uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100} />
                )}
              </div>
            )}

            {result && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-green-600">
                      <CheckCircle2 className="h-4 w-4" /> Criadas
                    </div>
                    <div className="mt-1 text-2xl font-semibold">{result.created_count}</div>
                  </div>
                  <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-yellow-700">
                      <AlertTriangle className="h-4 w-4" /> Puladas
                    </div>
                    <div className="mt-1 text-2xl font-semibold">{result.skipped_count}</div>
                  </div>
                  <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-red-600">
                      <XCircle className="h-4 w-4" /> Falhas
                    </div>
                    <div className="mt-1 text-2xl font-semibold">{result.failed_count}</div>
                  </div>
                </div>

                {result.created.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium">Páginas criadas</h3>
                    <div className="space-y-1 rounded border">
                      {result.created.map((r) => (
                        <div key={r.page_id} className="flex items-center justify-between gap-2 border-b px-3 py-2 text-sm last:border-b-0">
                          <span className="min-w-0 flex-1 truncate">{r.filename}</span>
                          <Badge variant="outline" className="text-xs">
                            {r.action === "new_version" ? "nova versão" : "criada"}
                          </Badge>
                          <Link
                            to={`/tenant/kb/${spaceId}/pages/${r.page_id}`}
                            className="flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            Abrir <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.skipped.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium">Puladas</h3>
                    <div className="space-y-1 rounded border">
                      {result.skipped.map((r, i) => (
                        <div key={i} className="flex items-center justify-between border-b px-3 py-2 text-sm last:border-b-0">
                          <span className="min-w-0 flex-1 truncate">{r.filename}</span>
                          <span className="text-xs text-muted-foreground">{r.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.failed.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium">Falhas</h3>
                    <div className="space-y-1 rounded border">
                      {result.failed.map((r, i) => (
                        <div key={i} className="flex items-center justify-between border-b px-3 py-2 text-sm last:border-b-0">
                          <span className="min-w-0 flex-1 truncate">{r.filename}</span>
                          <span className="text-xs text-red-600">{r.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetWizard}>Nova importação</Button>
                  <Button onClick={() => navigate(`/tenant/kb/${spaceId}`)}>
                    Ver space
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Footer (Voltar / Próximo) */}
      {!result && (
        <div className="flex items-center justify-between border-t pt-3">
          <Button
            variant="outline"
            disabled={step === 1 || running}
            onClick={() => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s))}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
          </Button>
          {step < 3 ? (
            <Button
              disabled={!canNext}
              onClick={() => setStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s))}
            >
              Próximo <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <div />
          )}
        </div>
      )}
    </div>
  );
}
